// useTiendaConfig — hook que lee todas las keys tienda_* de app_config y devuelve
// la configuración editorial parseada (con defaults razonables si una key falta).
//
// Editable desde /admin/tienda. Lectura pública (RLS policy app_config_public_read).
// Cache 60s en sessionStorage para evitar trips constantes.

import { useEffect, useState } from 'react'
import { supabase } from './supabase'

const STORAGE_KEY = 'limpack_tienda_config_v1'
const CACHE_MAX_AGE_MS = 60_000

const DEFAULTS = {
  hero: {
    eyebrow: 'La Tienda Limpack',
    titulo: 'Limpieza, resuelta cada mes.',
    subtitulo: 'Suscribite y llega solo cada mes, o armá tu carrito con productos sueltos.',
    ctaLabel: 'Ver los kits',
    ctaSecundarioLabel: 'Armá tu carrito',
    ctaSecundarioHref: '/tienda/catalogo',
  },
  categorias: [],
  kitsTierDefault: 'essential',
  empresas: {
    titulo: '¿Tu empresa también necesita?',
    subtitulo: 'Oficinas, coworks, hoteles, restaurantes. Cantidad pactada, factura electrónica DGI, entrega mensual.',
    ctaLabel: 'Pedir presupuesto',
    meta: '12 empresas activas · transferencia 30 días · mínimo $3.000/mes',
  },
  promoBar: [],
  // Palanca del flip de checkout: false = flujo viejo (CheckoutV5, día de ruta);
  // true = LimpackFlex OneShot (CheckoutOneShot). Se prende desde app_config sin redeploy.
  checkoutOneshotActivo: false,
}

function parseJson(raw, fallback) {
  if (!raw) return fallback
  try { return JSON.parse(raw) } catch { return fallback }
}

function rowsToConfig(rows) {
  const map = new Map(rows.map(r => [r.key, r.value]))
  return {
    hero: {
      eyebrow: map.get('tienda_hero_eyebrow') ?? DEFAULTS.hero.eyebrow,
      titulo: map.get('tienda_hero_titulo') ?? DEFAULTS.hero.titulo,
      subtitulo: map.get('tienda_hero_subtitulo') ?? DEFAULTS.hero.subtitulo,
      ctaLabel: map.get('tienda_hero_cta_label') ?? DEFAULTS.hero.ctaLabel,
      ctaSecundarioLabel: map.get('tienda_hero_cta_secundario_label') ?? DEFAULTS.hero.ctaSecundarioLabel,
      ctaSecundarioHref: map.get('tienda_hero_cta_secundario_href') ?? DEFAULTS.hero.ctaSecundarioHref,
    },
    categorias: parseJson(map.get('tienda_categorias_config'), DEFAULTS.categorias),
    kitsTierDefault: map.get('tienda_kits_tier_default') ?? DEFAULTS.kitsTierDefault,
    empresas: {
      titulo: map.get('tienda_empresas_titulo') ?? DEFAULTS.empresas.titulo,
      subtitulo: map.get('tienda_empresas_subtitulo') ?? DEFAULTS.empresas.subtitulo,
      ctaLabel: map.get('tienda_empresas_cta_label') ?? DEFAULTS.empresas.ctaLabel,
      meta: map.get('tienda_empresas_meta') ?? DEFAULTS.empresas.meta,
    },
    promoBar: parseJson(map.get('tienda_promo_bar'), DEFAULTS.promoBar),
    checkoutOneshotActivo: map.get('tienda_checkout_oneshot_activo') === 'true',
  }
}

export function useTiendaConfig() {
  const [config, setConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (!raw) return DEFAULTS
      const parsed = JSON.parse(raw)
      const age = Date.now() - (parsed.ts || 0)
      if (age > CACHE_MAX_AGE_MS) return DEFAULTS
      return parsed.config || DEFAULTS
    } catch {
      return DEFAULTS
    }
  })
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    let activo = true
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('app_config')
          .select('key,value')
          .like('key', 'tienda_%')
        if (!activo) return
        if (error) {
          setConfig(DEFAULTS)
        } else {
          const c = rowsToConfig(data || [])
          setConfig(c)
          try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ config: c, ts: Date.now() })) } catch {}
        }
      } catch {
        if (activo) setConfig(DEFAULTS)
      } finally {
        if (activo) setCargando(false)
      }
    })()
    return () => { activo = false }
  }, [])

  return { config, cargando }
}

// Helper para limpiar cache después de guardar desde el admin
export function invalidarCacheTiendaConfig() {
  try { sessionStorage.removeItem(STORAGE_KEY) } catch {}
}
